import React from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';

import MenuItem from 'components/Menu/MenuItem';
import MenuSectionGrid from 'components/Menu/MenuSectionGrid';
import {
  getMinimumFulfillmentDateTimeForItem,
  getItemLeadTimeText
} from 'catering/helpers/leadTime';
import { formatCateringLocationInventory } from 'catering/helpers/formatCateringLocationInventory';
import { useCart, useMenuItemDetailsBySlug } from 'catering/hooks/query';
import useProductHasSurchargeModifier from 'hooks/useProductHasSurchargeModifier';

function MenuSectionListItems({
  menuLeadTime,
  menuList,
  handleClick,
  fulfillmentDateTime,
  shouldLazyLoadMenu,
  locationInventory
}) {
  const { data: cart } = useCart();
  const formattedMenuList = formatCateringLocationInventory(
    locationInventory,
    menuList,
    cart
  );

  return (
    <MenuSectionGrid>
      {formattedMenuList.map(menuItem => (
        <MenuSectionListItem
          key={menuItem.id}
          menuItem={menuItem}
          menuLeadTime={menuLeadTime}
          fulfillmentDateTime={fulfillmentDateTime}
          handleClick={handleClick}
          shouldLazyLoadMenu={shouldLazyLoadMenu}
        />
      ))}
    </MenuSectionGrid>
  );
}

MenuSectionListItems.propTypes = {
  menuList: PropTypes.array,
  handleClick: PropTypes.func.isRequired,
  shouldLazyLoadMenu: PropTypes.bool,
  menuLeadTime: PropTypes.shape({
    lead_time_days: PropTypes.number,
    lead_time_hours: PropTypes.number,
    cutoff_time: PropTypes.string
  }).isRequired,
  fulfillmentDateTime: PropTypes.instanceOf(Date),
  locationInventory: PropTypes.object
};

const MenuSectionListItem = ({
  menuItem,
  menuLeadTime,
  fulfillmentDateTime,
  handleClick,
  shouldLazyLoadMenu
}) => {
  const minimumFulfillmentDateTimeForItem = getMinimumFulfillmentDateTimeForItem(
    menuItem,
    menuLeadTime
  );
  const { data: menuItemDetail } = useMenuItemDetailsBySlug(
    menuItem.product.slug
  );
  const productHasSurchargeModifier = useProductHasSurchargeModifier(
    menuItemDetail
  );
  const isItemFulfillmentDateTimeGreaterThanSelected =
    minimumFulfillmentDateTimeForItem > fulfillmentDateTime;
  const handleClickWithLeadTime = () => {
    if (isItemFulfillmentDateTimeGreaterThanSelected) {
      handleClick(
        menuItem.product.slug,
        minimumFulfillmentDateTimeForItem.getTime()
      );
    } else {
      handleClick(menuItem.product.slug);
    }
  };

  return (
    <MenuItem
      {...menuItem.product}
      handleClick={handleClickWithLeadTime}
      shouldLazyLoadMenu={shouldLazyLoadMenu}
      leadTimeText={
        isItemFulfillmentDateTimeGreaterThanSelected
          ? getItemLeadTimeText(menuItem, menuLeadTime)
          : ''
      }
      inventory={menuItem.inventory}
      inventoryLessCart={menuItem.inventoryLessCart}
      lowInventoryCount={menuItem.lowInventoryCount}
      productHasSurchargeModifier={productHasSurchargeModifier}
    />
  );
};

MenuSectionListItem.propTypes = {
  menuItem: PropTypes.shape({
    id: PropTypes.number,
    product: PropTypes.object,
    inventory: PropTypes.number,
    inventoryLessCart: PropTypes.number,
    lowInventoryCount: PropTypes.number
  }).isRequired,
  menuLeadTime: PropTypes.shape({
    lead_time_days: PropTypes.number,
    lead_time_hours: PropTypes.number,
    cutoff_time: PropTypes.string
  }).isRequired,
  fulfillmentDateTime: PropTypes.instanceOf(Date),
  handleClick: PropTypes.func.isRequired,
  shouldLazyLoadMenu: PropTypes.bool,
  locationInventory: PropTypes.object
};

function MenuSectionList({
  isExpanded,
  menuList,
  handleClick,
  shouldLazyLoadMenu,
  menuLeadTime,
  fulfillmentDateTime,
  locationInventory
}) {
  return (
    <Collapse in={isExpanded}>
      <MenuSectionListItems
        isExpanded={isExpanded}
        menuLeadTime={menuLeadTime}
        menuList={menuList}
        handleClick={handleClick}
        fulfillmentDateTime={fulfillmentDateTime}
        shouldLazyLoadMenu={shouldLazyLoadMenu}
        locationInventory={locationInventory}
      />
    </Collapse>
  );
}

MenuSectionList.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  menuList: PropTypes.array,
  handleClick: PropTypes.func.isRequired,
  shouldLazyLoadMenu: PropTypes.bool,
  menuLeadTime: PropTypes.object.isRequired,
  fulfillmentDateTime: PropTypes.instanceOf(Date),
  locationInventory: PropTypes.object
};

MenuSectionList.defaultProps = {
  menuLeadTime: {}
};

export default MenuSectionList;
